import * as _ from 'lodash-es';
import React, { useContext, useEffect, useState } from 'react';
import { POST_SIDEBAR_SHIFT_BREAKPOINT } from '../../post/PostLayoutSizes';
import { MIN_MAX_WIDTHS } from '../../utils/responsive/Sizes';
import useRouteHandle from '../useRouteHandle';

export const LAYOUT_DEBOUNCE_TIMEOUT = 500;
const DEFAULT_SHIFT_BREAKPOINT = MIN_MAX_WIDTHS.sm[1];
const SHIFT_BREAKPOINTS: Record<string, number> = {
  post: POST_SIDEBAR_SHIFT_BREAKPOINT,
  'post-history': POST_SIDEBAR_SHIFT_BREAKPOINT,
  'comment-history': MIN_MAX_WIDTHS.md[1],
};

const SidebarShiftBreakpointContext = React.createContext(
  DEFAULT_SHIFT_BREAKPOINT,
);
export const useSidebarShiftBreakpoint = () =>
  useContext(SidebarShiftBreakpointContext);

const ShouldShiftContentForSidebarContext = React.createContext(false);
export const useShouldShiftContentForSidebar = () =>
  useContext(ShouldShiftContentForSidebarContext);

interface SidebarShiftStateProviderProps {
  children: React.ReactNode;
}

const SidebarShiftStateProvider = ({
  children,
}: SidebarShiftStateProviderProps) => {
  const { routeName } = useRouteHandle();
  const shiftBreakpoint =
    SHIFT_BREAKPOINTS[routeName] ?? DEFAULT_SHIFT_BREAKPOINT;

  const [shouldShiftContentForSidebar, setShouldShiftContentForSidebar] =
    useState(() => window.innerWidth < shiftBreakpoint);

  useEffect(() => {
    setShouldShiftContentForSidebar(window.innerWidth < shiftBreakpoint);

    let previousWidth = window.innerWidth;
    const handleResize = _.debounce(() => {
      if (
        previousWidth >= shiftBreakpoint &&
        window.innerWidth < shiftBreakpoint
      ) {
        setShouldShiftContentForSidebar(true);
      } else if (
        previousWidth < shiftBreakpoint &&
        window.innerWidth >= shiftBreakpoint
      ) {
        setShouldShiftContentForSidebar(false);
      }
      previousWidth = window.innerWidth;
    }, LAYOUT_DEBOUNCE_TIMEOUT);

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [shiftBreakpoint]);

  return (
    <ShouldShiftContentForSidebarContext.Provider
      value={shouldShiftContentForSidebar}
    >
      <SidebarShiftBreakpointContext.Provider value={shiftBreakpoint}>
        {children}
      </SidebarShiftBreakpointContext.Provider>
    </ShouldShiftContentForSidebarContext.Provider>
  );
};

export default SidebarShiftStateProvider;
